// TabContent1.js
import React, { useState, useEffect } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, InputBase } from '@mui/material';
import { tokens } from '../../../../../theme';
import NumberInput from '../../../../theme/inputs/NumberInput';
import ShDateRangePicker from "../../../../theme/ShDateRangePicker";
import ShValidatedInput from '../../../../theme/inputs/ShValidatedInput';
import { ENV_TYPES } from "../../../Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import * as yup from "yup";
import { yupNumberRequired, yupRequired } from '../../../../../utils/validation';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useGetRelationsSelectQuery } from '../../../../../newapi/global/relation/relationSlice';
import { useGetWebshopCredentialsSelectQuery } from '../../../../../newapi/webshop/webshopSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import ShSwitch from "../../../../theme/inputs/ShSwitch";

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
 const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();

    
    const handleInput = (key, val) => {
        if (key.indexOf('.') > -1) {
            key = key.split('.')[1];

            const shippingAddress = data.shippingAddress ? { ...data.shippingAddress } : {};
            shippingAddress[key] = val;
            props.handleDataUpdate({ shippingAddress: shippingAddress });
            dispatch(setTabErrors({ ...props.errors, [key]: '' }));
        } else {
            props.handleDataUpdate({ [key]: val });
            dispatch(setTabErrors({ ...props.errors, [key]: '' }));
        }
    };

    
    return (
        <>
            <Box 
                sx={{
                    display:'flex',
                    flexDirection:"column",
                }}
            >
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("first_name")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.firstName"
                            value={data.shippingAddress ? data.shippingAddress.firstName : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.firstName`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("last_name")}</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.lastName"
                            value={data.shippingAddress ? data.shippingAddress.lastName : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.lastName`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("company")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.company"
                            value={data.shippingAddress ? data.shippingAddress.company : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.company`]}
                        />
                    </Box>
                </Box>
                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("phone")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="shippingAddress.phone"
                            value={data.shippingAddress ? data.shippingAddress.phone : ''}
                            onChange={handleInput}
                            error={props.errors[`shippingAddress.phone`]}
                        />
                    </Box>
                </Box>

                <Box 
                    sx={{
                        width:1,
                        paddingTop: 1.5,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >
                    <Box
                        sx={{
                            width:140,
                        }}
                    >
                        <Text>{t("email")} ({t("_optional")})</Text>
                    </Box>
                    
                    <Box
                        sx={{
                            flex:1,
                            borderRadius:4,
                            display:"flex",
                        }}
                    >
                        <ShValidatedInput
                            name="email"
                            value={data.email ?? ''}
                            onChange={handleInput}
                            error={props.errors.email}
                        />
                    </Box>
                </Box>
                
               
            </Box>
        </>
    );
};

const Details = (props) => {
    const {t} = useTranslation();

    const addressFields = [
        'firstName',
        'lastName'
    ];
    
    const conditionalSchema = [
        ...addressFields.map(field => ({
            key: `shippingAddress.${field}`,  // Use shippingAddress prefix
            errorField: t(field),
            alwaysRequired: false,
            condition: (data) => true,
            type: "text"
        }))
    ];

    return (
        <ProgressionTab 
            tabId="tabDetails" 
            subtitle={t("Details")} 
            step={1}
            nextTab={"tabAddressDetails"}
            maxSteps={3}
            conditionalSchema={conditionalSchema}
        >
            <Content/>
        </ProgressionTab>
    )
}

export default Details;

